import React, { createContext, useReducer, useContext, Dispatch } from "react";
import { getAlternateEmails } from "services/alternateEmailService";
import toast from "react-hot-toast";

interface AlternateEmail {
  id?: number;
  userId?: number;
  email: string;
  aptReminderEmail?: number;
  aptCancellationEmail?: number;
  aptTransferEmail?: number;
  aptOpeningEmail?: number;
  ofcMessageEmail?: number;
  isActive?: number;
  isVerified?: number;
  createdAt?: string;
  updatedAt?: string;
}

interface OfficeNotificationSettings {
  id?: number;
  userId?: number;
  aptReminderEmail?: number;
  aptReminderSms?: number;
  aptCancellationEmail?: number;
  aptCancellationSms?: number;
  aptTransferEmail?: number;
  aptTransferSms?: number;
  aptOpeningEmail?: number;
  aptOpeningSms?: number;
  ofcMessageSms?: number;
  userRoleId?: number;
}

interface AlternateEmailState {
  emails: AlternateEmail[];
  lastFetchedAt: number | null;
  isLoading: boolean;
  officeNotificationSettings: OfficeNotificationSettings | null;
}

type Action =
  | { type: "SET_EMAILS"; payload: AlternateEmail[] }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "RESET" }
  | {
      type: "SET_OFFICE_NOTIFICATION_SETTINGS";
      payload: OfficeNotificationSettings | null;
    };

const initialState: AlternateEmailState = {
  emails: [],
  lastFetchedAt: null,
  isLoading: false,
  officeNotificationSettings: null,
};

const AlternateEmailContext = createContext<{
  state: AlternateEmailState;
  dispatch: Dispatch<Action>;
  fetchAlternateEmails: (refetch?: boolean) => Promise<void>;
} | null>(null);

function reducer(
  state: AlternateEmailState,
  action: Action
): AlternateEmailState {
  switch (action.type) {
    case "SET_EMAILS":
      return {
        ...state,
        emails: action.payload,
        lastFetchedAt: Date.now(),
        isLoading: false,
      };
    case "SET_LOADING":
      return { ...state, isLoading: action.payload };
    case "RESET":
      return initialState;
    case "SET_OFFICE_NOTIFICATION_SETTINGS":
      return {
        ...state,
        officeNotificationSettings: action.payload,
      };
    default:
      return state;
  }
}

export const AlternateEmailProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const fetchAlternateEmails = async (refetch = false) => {
    try {
      const TEN_MINUTES = 10 * 60 * 1000;
      const shouldRefetch =
        refetch ||
        !state.lastFetchedAt ||
        Date.now() - state.lastFetchedAt > TEN_MINUTES;

      if (!shouldRefetch) return;

      dispatch({ type: "SET_LOADING", payload: true });

      const response = await getAlternateEmails();
      if (response?.error) {
        toast.error(response.error || "Failed to load alternate emails");
        return;
      }

      dispatch({ type: "SET_EMAILS", payload: response || [] });
    } catch (error) {
      console.error("Error fetching alternate emails:", error);
      toast.error("Error fetching alternate emails");
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  };

  return (
    <AlternateEmailContext.Provider
      value={{ state, dispatch, fetchAlternateEmails }}
    >
      {children}
    </AlternateEmailContext.Provider>
  );
};

export const useAlternateEmailContext = () => {
  const context = useContext(AlternateEmailContext);
  if (!context) {
    throw new Error(
      "useAlternateEmailContext must be used within AlternateEmailProvider"
    );
  }
  return context;
};
